import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import LogoutIcon from '@mui/icons-material/Logout';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { BsGrid3X3GapFill } from "react-icons/bs";
import { HiUsers } from 'react-icons/hi2';
import { FaBasketShopping, FaFilePrescription } from "react-icons/fa6";
import { Pagination } from 'antd';
import { getMediaByFolder } from '../Api';
import { toast } from 'react-toastify';
import { FaFileAlt, FaLocationArrow } from 'react-icons/fa';
import { BiSolidCoupon } from 'react-icons/bi';

// Navigation configuration
const NAVIGATION = [
    {
        kind: 'header',
        title: 'Main items',
    },
    {
        segment: 'dashboard',
        title: 'Dashboard',
        icon: <DashboardIcon />,
    },
    {
        segment: 'dashboard-products',
        title: 'Products',
        icon: <FaBasketShopping style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-users',
        title: 'Users',
        icon: <HiUsers style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-categories',
        title: 'Categories',
        icon: <BsGrid3X3GapFill style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-orders',
        title: 'Orders',
        icon: <ShoppingCartIcon />,
    },
    {
        segment: 'prescriptions',
        title: 'Prescriptions',
        icon: <FaFilePrescription style={{ fontSize: '20px' }} />,
    },
    {
        segment: 'dashboard-coupons',
        title: 'Coupons',
        icon: <BiSolidCoupon style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-content',
        title: 'Content',
        icon: <FaFileAlt style={{ fontSize: '20px' }} />,
    },
    {
        segment: 'dashboard-location',
        title: 'Location',
        icon: <FaLocationArrow />,
    },
    {
        kind: 'divider',
    },
    {
        kind: 'header',
        title: 'Analytics',
    },
    {
        segment: 'reports',
        title: 'Reports',
        icon: <BarChartIcon />,
        children: [
            {
                segment: 'sales',
                title: 'Sales',
                icon: <DescriptionIcon />,
            },
            {
                segment: 'traffic',
                title: 'Traffic',
                icon: <DescriptionIcon />,
            },
        ],
    },
    {
        segment: 'integrations',
        title: 'Integrations',
        icon: <LayersIcon />,
    },
];

// Styled Skeleton component
const Skeleton = styled('div')(({ theme, height }) => ({
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    height,
    content: '" "',
}));

// Custom router hook
function useDemoRouter(initialPath) {
    const [pathname, setPathname] = React.useState(initialPath);
    const navigate = useNavigate();

    const router = React.useMemo(() => {
        return {
            pathname,
            searchParams: new URLSearchParams(),
            navigate: (path) => {
                setPathname(String(path));
                navigate(path);
            },
        };
    }, [pathname, navigate]);

    return router;
}

// Create a light theme
const theme = createTheme({
    palette: {
        mode: 'light',
    },
});

function Prescriptions({ onLogout }) {
    const navigate = useNavigate();
    const router = useDemoRouter('/prescriptions');
    const [loading, setLoading] = useState(false);
    const [token] = useState(localStorage.getItem('auth_token'));
    const [prescriptions, setPrescriptions] = useState([]);
    const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        async function fetchData() {
            if (!token) {
                toast.error('Authentication token missing. Please log in again.');
                navigate('/login');
                return;
            }
            setLoading(true);
            try {
                const response = await getMediaByFolder('Prescription', page, itemsPerPage);
                console.log(response);

                setPrescriptions(Array.isArray(response.data) ? response.data : []);
                setTotalPages(Number(response.totalPages) || 1);
            } catch (error) {
                console.error('Error fetching prescriptions:', error);
                toast.error(error.message || 'Failed to fetch prescriptions. Please try again.');
                setPrescriptions([]);
                setTotalPages(1);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [page, token, navigate]);

    const handlePageChange = (page) => {
        setPage(page);
    };

    const handleLogoutClick = () => {
        setOpenLogoutDialog(true);
    };

    const handleLogoutConfirm = () => {
        setOpenLogoutDialog(false);
        onLogout();
        navigate('/');
    };

    const handleLogoutCancel = () => {
        setOpenLogoutDialog(false);
    };

    const branding = {
        logo: (
            <div className="header-logo">
                <img onClick={() => navigate('/')} src={require('../images/logo.png')} alt="" style={{ height: '52px', width: '60px' }} />
            </div>
        ),
        title: '',
    };

    const ToolbarActions = () => (
        <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogoutClick}
            sx={{ marginRight: 2 }}
            style={{ background: "#0165AB", color: "#fff" }}
        >
            Logout
        </Button>
    );

    return (
        <AppProvider
            navigation={NAVIGATION}
            router={router}
            theme={theme}
            branding={branding}
        >
            <DashboardLayout
                slots={{
                    toolbarActions: ToolbarActions,
                }}
            >
                <PageContainer>
                    {loading ? (
                        <Grid container spacing={1}>
                            <Grid size={12} height={5} />
                            <Grid size={12}>
                                <Skeleton height={14} />
                            </Grid>
                            <Grid size={12}>
                                <Skeleton height={14} />
                            </Grid>
                            <Grid size={4}>
                                <Skeleton height={100} />
                            </Grid>
                            <Grid size={8}>
                                <Skeleton height={100} />
                            </Grid>
                            <Grid size={12}>
                                <Skeleton height={150} />
                            </Grid>
                            <Grid size={12}>
                                <Skeleton height={14} />
                            </Grid>
                            <Grid size={3}>
                                <Skeleton height={100} />
                            </Grid>
                            <Grid size={3}>
                                <Skeleton height={100} />
                            </Grid>
                            <Grid size={3}>
                                <Skeleton height={100} />
                            </Grid>
                            <Grid size={3}>
                                <Skeleton height={100} />
                            </Grid>
                        </Grid>
                    ) : (
                        <section className="admin-dashboard">
                            <Grid container spacing={1}>
                                <Grid size={12} height={5} />
                                <Grid size={12}>
                                    <table className="user-detail-table">
                                        <thead>
                                            <tr>
                                                <th>Media ID</th>
                                                <th>User ID</th>
                                                <th>Upload Date (M/D/Y)</th>
                                                <th>Prescription File</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {prescriptions.length > 0 ? (
                                                prescriptions.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{item.id}</td>
                                                        <td>{item.author || 'Unknown'}</td>
                                                        <td>{new Date(item.date).toLocaleDateString()}</td>
                                                        <td>
                                                            <a
                                                                href={item.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: "black", fontSize: "24px" }}
                                                                title="View Prescription"
                                                            >
                                                                <FaFilePrescription />
                                                            </a>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4">No prescriptions found!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </Grid>
                                <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Pagination
                                        current={page}
                                        total={totalPages * itemsPerPage}
                                        pageSize={itemsPerPage}
                                        onChange={handlePageChange}
                                        showSizeChanger={false}
                                    />
                                </Grid>
                            </Grid>
                        </section>
                    )}
                </PageContainer>
            </DashboardLayout>
            <Dialog
                open={openLogoutDialog}
                onClose={handleLogoutCancel}
                aria-labelledby="logout-dialog-title"
                aria-describedby="logout-dialog-description"
            >
                <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
                <DialogContent>
                    <DialogContentText id="logout-dialog-description">
                        Are you sure you want to log out?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleLogoutCancel}>Cancel</Button>
                    <Button onClick={handleLogoutConfirm} autoFocus>
                        Logout
                    </Button>
                </DialogActions>
            </Dialog>
        </AppProvider>
    );
}

export default Prescriptions;